/**
 * Client-side settings API module — replaces settings.action.ts Server Actions.
 */

import { SETTINGS } from "@/shared/constants/backend-api"
import { authFetchJson, getUserIdFromToken } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { TwoFactAuthFormType } from "../schemas/two-fact-auth-form.schema"
import { SettingsResponse } from "../types/settings.type"
import { NotificationSettingsFormType } from "../schemas/setting-notifications-form.schema"
import { SettingVerifyCodeFormType } from "../schemas/setting-verify-code-form.schema"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const SETTINGS_QUERY_KEYS = {
  all: ["settings"] as const,
  detail: (userId: number) => ["settings", userId] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getSettings(
  userId?: number,
): Promise<ApiResponse<SettingsResponse>> {
  const uid = userId !== undefined ? userId : getUserIdFromToken()
  return authFetchJson<SettingsResponse>(SETTINGS.byId(uid))
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export async function setupTwoFactAuth(
  userIdOrData: number | TwoFactAuthFormType,
  data?: TwoFactAuthFormType,
): Promise<ApiResponse<TwoFactAuthFormType>> {
  let uid: number
  let finalData: TwoFactAuthFormType
  if (typeof userIdOrData === "object") {
    uid = getUserIdFromToken()
    finalData = userIdOrData
  } else {
    uid = userIdOrData
    finalData = data!
  }
  return authFetchJson<TwoFactAuthFormType>(SETTINGS.setupTwoFactAuth(uid), {
    method: "POST",
    body: JSON.stringify(finalData),
  })
}

export async function updateNotifications(
  userIdOrData: number | NotificationSettingsFormType,
  data?: NotificationSettingsFormType,
): Promise<ApiResponse<NotificationSettingsFormType>> {
  let uid: number
  let finalData: NotificationSettingsFormType
  if (typeof userIdOrData === "object") {
    uid = getUserIdFromToken()
    finalData = userIdOrData
  } else {
    uid = userIdOrData
    finalData = data!
  }
  return authFetchJson<NotificationSettingsFormType>(SETTINGS.byId(uid), {
    method: "PUT",
    body: JSON.stringify(finalData),
  })
}

export async function verifyTwoFactAuthCode(
  userIdOrData: number | SettingVerifyCodeFormType,
  data?: SettingVerifyCodeFormType,
): Promise<ApiResponse<SettingVerifyCodeFormType>> {
  let uid: number
  let finalData: SettingVerifyCodeFormType
  if (typeof userIdOrData === "object") {
    uid = getUserIdFromToken()
    finalData = userIdOrData
  } else {
    uid = userIdOrData
    finalData = data!
  }
  return authFetchJson<SettingVerifyCodeFormType>(SETTINGS.verifyTwoFactAuthCode(uid), {
    method: "POST",
    body: JSON.stringify(finalData),
  })
}
